import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';

// Valid column keys that can be used
export type OrderColumnKey =
  | 'ref'
  | 'driver'
  | 'sender'
  | 'receiver'
  | 'customer'
  | 'destinations'
  | 'payment'
  | 'price'
  | 'distance'
  | 'status'
  | 'podRef'
  | 'date'
  | 'actions';

export interface OrderTableColumn {
  key: OrderColumnKey;
  label?: string; // Optional - auto-translated if not provided
  sortable?: boolean;
  className?: string;
  visible?: boolean;
  /**
   * Custom render function for the column
   * If not provided, OrderTableCell uses its default rendering
   */
  render?: (order: Order, displayForUser: 'customer' | 'carrier') => React.ReactNode;
}

export interface UseOrderTableColumnsConfig {
  displayForUser: 'customer' | 'carrier';
  customColumns?: OrderTableColumn[];
  includeActions?: boolean;
}

/**
 * Default column labels - used when label is not provided
 */
export const defaultColumnLabels: Record<OrderColumnKey, string> = {
  ref: 'table.columns.ref',
  driver: 'table.columns.driver',
  sender: 'table.columns.sender',
  receiver: 'table.columns.receiver',
  customer: 'table.columns.customer',
  destinations: 'table.columns.route',
  payment: 'table.columns.cost',
  price: 'table.columns.price',
  distance: 'table.columns.distance',
  status: 'table.columns.status',
  podRef: 'table.columns.my_ref',
  date: 'table.columns.date',
  actions: 'table.columns.actions',
};

/**
 * Default column classNames for proper sizing and layout.
 * Applied when the column does not have a custom className.
 */
// Padding / alignment only — column widths come from `OrderTableColGroup`.
const defaultColumnClassNames: Partial<Record<OrderColumnKey, string>> = {
  ref: 'align-middle !px-3',
  date: 'align-middle whitespace-nowrap !px-3',
  status: 'align-middle !ps-2 !pe-2',
  destinations: 'align-middle !pe-2 !ps-3',
  driver: 'align-middle whitespace-nowrap !ps-12 !pe-2',
  podRef: 'align-middle whitespace-nowrap !px-3',
  price: 'align-middle whitespace-nowrap !ps-2 !pe-3',
  actions: 'align-middle !px-3',
};

/**
 * Default columns for customer view
 * Order: ref (+client), destinations/route (+pickup date), status, driver, price, actions
 */
const getCustomerDefaultColumns = (): OrderTableColumn[] => [
  { key: 'ref', sortable: false, visible: true },
  { key: 'destinations', sortable: false, visible: true },
  { key: 'status', sortable: false, visible: true },
  { key: 'driver', sortable: false, visible: true },
  { key: 'price', sortable: false, visible: true },
];

/**
 * Default columns for carrier view
 * Order: ref (+client), destinations/route (+pickup date), status, driver, price, actions
 */
const getCarrierDefaultColumns = (): OrderTableColumn[] => [
  { key: 'ref', sortable: false, visible: true },
  { key: 'destinations', sortable: false, visible: true },
  { key: 'status', sortable: false, visible: true },
  { key: 'driver', sortable: false, visible: true },
  { key: 'price', sortable: false, visible: true },
];

/**
 * Hook for managing Order Table columns with default configurations
 *
 * Provides automatic column setup based on user role (customer/carrier)
 * while allowing full customization
 *
 * Note: This hook only handles column configuration (labels, visibility, order).
 * The actual rendering logic is in OrderTableCell component.
 *
 * @example Simple - just keys, auto-translated labels
 * ```tsx
 * const { columns } = useOrderTableColumns({
 *   displayForUser: 'customer',
 *   customColumns: [
 *     { key: 'ref' },  // Auto-translated
 *     { key: 'driver' }, // Auto-translated
 *     { key: 'status' }, // Auto-translated
 *   ],
 * });
 * ```
 *
 * @example With custom labels
 * ```tsx
 * const { columns } = useOrderTableColumns({
 *   displayForUser: 'customer',
 *   customColumns: [
 *     { key: 'ref', label: 'Order #' },  // Custom label
 *     { key: 'driver' }, // Auto-translated
 *   ],
 * });
 * ```
 *
 * @example With custom render
 * ```tsx
 * const { columns } = useOrderTableColumns({
 *   displayForUser: 'customer',
 *   customColumns: [
 *     {
 *       key: 'ref',
 *       label: 'Order #',
 *       render: (order) => <strong>{order.ref.short}</strong>
 *     },
 *   ],
 * });
 * ```
 */
export const useOrderTableColumns = ({
  displayForUser,
  customColumns,
  includeActions = true,
}: UseOrderTableColumnsConfig) => {
  const { t } = useTranslation('m-order');

  const defaultColumns = useMemo((): OrderTableColumn[] => {
    return displayForUser === 'customer' ? getCustomerDefaultColumns() : getCarrierDefaultColumns();
  }, [displayForUser]);

  // Use custom columns if provided, otherwise use defaults
  const columns = useMemo(() => {
    const cols = customColumns || defaultColumns;

    // Add actions column if includeActions is true and it's not already in the columns
    const hasActionsColumn = cols.some(col => col.key === 'actions');
    const finalCols = [...cols];

    if (includeActions && !hasActionsColumn) {
      finalCols.push({
        key: 'actions',
        sortable: false,
        visible: true,
      });
    }

    // Auto-translate labels and apply default classNames if not provided
    return finalCols
      .filter(col => col.visible !== false)
      .map(col => ({
        ...col,
        label: col.label || t(defaultColumnLabels[col.key], { defaultValue: col.key }),
        className: col.className || defaultColumnClassNames[col.key] || '',
      }));
  }, [customColumns, defaultColumns, includeActions, t]);

  return {
    columns,
    defaultColumns,
  };
};
